import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
 ChevronLeft, Calendar, CheckCircle2, 
 XCircle, Clock, RefreshCw, Filter,
 TrendingUp, BarChart3, ShieldCheck, Activity, Zap, Search
} from 'lucide-react';
import { useGetStudentProfile, useGetDetailedAttendance } from '../../hooks/useQueries';

const StudentAttendance = () => {
 const navigate = useNavigate();
 const [userEmail, setUserEmail] = useState<string | null>(null);

 // 1. Identify User
 useEffect(() => {
  const checkUser = async () => {
   const { data: { user } } = await supabase.auth.getUser();
   if (user?.email) {
    setUserEmail(user.email);
   } else {
    navigate('/');
   }
  };
  checkUser();
 }, [navigate]);

 // 2. ✅ Persistent Hooks for Offline Support
 const { data: student, isLoading: profileLoading } = useGetStudentProfile(userEmail || '');
 const studentId = student?.student_id || student?.id;
 
 const { data: records = [], isLoading: attLoading } = useGetDetailedAttendance(studentId);

 const isLoading = profileLoading || attLoading;

 // 3. Compute Stats from Records
 const present = records.filter((r: any) => r.status === 'P' || r.status === 'Present').length;
 const total = records.length;
 const percentage = total > 0 ? Math.round((present / total) * 100) : 0;
 const stats = {
  present,
  absent: total - present,
  total,
  percentage
 };

 if (isLoading && !studentId) return (
  <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
    <div className="relative">
     <RefreshCw size={60} className="animate-spin text-blue-600/20"/>
     <Activity size={30} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-600" />
    </div>
    <p className="font-black  text-slate-400 text-[10px] mt-8 uppercase">Loading Attendance...</p>
  </div>
 );

 return (
  <div className="min-h-screen bg-slate-50 py-12 px-4 md:px-10 pb-32 font-inter">
   <div className="max-w-full mx-auto space-y-12">
    
    {/* --- NAVIGATION & CONTEXT --- */}
    <div className="flex justify-between items-center">
     <button 
      onClick={() => navigate(-1)} 
      className="group flex items-center gap-3 bg-white px-6 py-3 rounded-[5px] shadow-sm border border-slate-100 hover:shadow-2xl active:scale-95 tracking-widest hover:border-blue-200 transition-all active:scale-95"
     >
      <ChevronLeft size={18} className="text-blue-600 group-hover:-translate-x-1 transition-transform" />
      <span className="font-black tracking-widest text-[10px] text-slate-600 uppercase">Back</span>
     </button>

     <div className="bg-slate-900 px-6 py-3 rounded-[5px] border border-slate-800 shadow-2xl active:scale-95 tracking-widest flex items-center gap-4 group">
       <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
       <span className="text-[10px] font-black  text-blue-400 uppercase">Live Updates Active</span>
     </div>
    </div>

    {/* --- DYNAMIC HEADER --- */}
    <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-10">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="">
       <h1 className="text-5xl md:text-7xl font-black text-slate-900  leading-none uppercase">
        Attendance<br/>
        <span className="text-blue-600">History</span>
       </h1>
       <p className="text-slate-400 font-black text-[10px] mt-4 flex items-center gap-2 uppercase">
        <ShieldCheck size={12} className="text-blue-500" /> Complete school attendance record
       </p>
      </motion.div>
      
      <div className="bg-white border border-slate-100 rounded-[5px] p-6 shadow-sm flex items-center gap-8 group hover:shadow-2xl active:scale-95 tracking-widest transition-all">
       <div className="w-16 h-16 bg-slate-900 rounded-[1.5rem] flex items-center justify-center text-3xl shadow-2xl active:scale-95 tracking-widest shadow-slate-200 group-hover:scale-110 transition-transform">📅</div>
       <div>
        <p className="text-[9px] font-black text-slate-400  mb-1 uppercase">Attendance</p>
        <p className="text-3xl font-black text-slate-900 uppercase">{stats.percentage}% Consistent</p>
       </div>
      </div>
    </div>

    {/* 🟢 ANALYTICS SUMMARY GRID */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <PremiumStatBox label="Monthly Score" value={`${stats.percentage}%`} icon={TrendingUp} accent="blue" />
      <PremiumStatBox label="Total Days" value={stats.total} icon={BarChart3} accent="slate" />
      <PremiumStatBox label="Days Present" value={stats.present} icon={CheckCircle2} accent="emerald" />
      <PremiumStatBox label="Days Absent" value={stats.absent} icon={XCircle} accent="rose" />
    </div>

    {/* 🔵 PRESENCE TIMELINE */}
    <motion.div 
     initial={{ opacity: 0, y: 20 }}
     animate={{ opacity: 1, y: 0 }}
     transition={{ delay: 0.2 }}
     className="bg-white rounded-[5px] shadow-sm border border-slate-100 overflow-hidden group"
    >
      <div className="p-10 border-b border-slate-50 bg-slate-50/30 flex flex-col md:flex-row justify-between items-center gap-6 px-12">
       <h3 className="font-black text-[10px] text-slate-400  flex items-center gap-3 uppercase">
         <Clock size={16} className="text-blue-600"/> Attendance History
       </h3>
       <div className="flex items-center gap-4">
         <div className="relative group/filter">
          <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within/filter:text-blue-500 transition-colors" size={14}/>
          <select className="bg-white border border-slate-100 rounded-[5px] pl-10 pr-6 py-2 text-[9px] font-black tracking-widest outline-none focus:ring-4 focus:ring-blue-100 transition-all appearance-none uppercase">
            <option>Full List</option>
            <option>Last 30 Days</option>
            <option>Absents Only</option>
          </select>
         </div>
       </div>
      </div>

      <div className="p-6 md:p-12">
       {records.length > 0 ? (
        <div className="grid gap-6">
          {records.map((record: any, idx: number) => (
           <motion.div 
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.05 * idx }}
            key={idx} 
            className={`bg-white rounded-[5px] p-8 border transition-all duration-500 flex flex-col md:flex-row items-center justify-between group/row shadow-sm hover:shadow-2xl active:scale-95 tracking-widest ${
              record.status === 'P' || record.status === 'Present' ? 'hover:border-blue-100 border-slate-50' : 'hover:border-rose-100 border-rose-50 shadow-rose-500/5 bg-rose-50/10'
            }`}
           >
            <div className="flex items-center gap-8 w-full md:w-auto">
              <div className={`w-16 h-16 rounded-[5px] flex items-center justify-center transition-colors shadow-sm ${
               record.status === 'P' || record.status === 'Present' ? 'bg-blue-50 text-blue-600 group-hover/row:bg-blue-600 group-hover/row:text-white' : 'bg-rose-50 text-rose-600 group-hover/row:bg-rose-600 group-hover/row:text-white'
              }`}>
               <Calendar size={24}/>
              </div>
              <div className="space-y-1">
               <p className="font-black text-slate-900 text-xl uppercase">
                 {new Date(record.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
               </p>
               <p className="text-[9px] font-black text-slate-400  flex items-center gap-2 uppercase">
                 <Zap size={10} className="text-blue-500"/> {new Date(record.date).toLocaleDateString('en-GB', { weekday: 'long' })}
               </p>
              </div>
            </div>

            <div className="mt-6 md:mt-0 flex items-center gap-6 w-full md:w-auto">
              <div className={`px-10 py-4 rounded-[1.5rem] font-black text-[10px]  shadow-2xl active:scale-95 tracking-widest transition-all duration-500 uppercase ${
               record.status === 'P' || record.status === 'Present'
               ? 'bg-slate-900 text-white shadow-slate-200 group-hover/row:bg-blue-600' 
               : 'bg-rose-600 text-white shadow-rose-200 animate-pulse'
              }`}>
               {record.status === 'P' || record.status === 'Present' ? 'Present' : 'Absent'}
              </div>
            </div>
           </motion.div>
          ))}
        </div>
       ) : (
        <div className="py-32 text-center space-y-8 bg-slate-50/50 rounded-[5px] border border-dashed border-slate-200 opacity-30 group">
          <div className="w-24 h-24 bg-white rounded-[5px] flex items-center justify-center mx-auto mb-4 text-5xl shadow-inner group-hover:rotate-12 transition-transform duration-500">📅</div>
          <div className="space-y-2">
           <h4 className="text-xl font-black text-slate-900 uppercase">No Records Found</h4>
           <p className="text-[9px] font-black text-slate-400 uppercase">No attendance records found for this month.</p>
          </div>
        </div>
       )}
      </div>
    </motion.div>
   </div>
  </div>
 );
};

const PremiumStatBox = ({ label, value, icon: Icon, accent }: any) => {
  const colors: any = {
   blue: 'bg-blue-50 text-blue-600 border-blue-100',
   emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
   rose: 'bg-rose-50 text-rose-600 border-rose-100',
   slate: 'bg-slate-100 text-slate-600 border-slate-200'
  };

  return (
   <div className="bg-white p-8 rounded-[5px] shadow-sm border border-slate-100 hover:shadow-2xl active:scale-95 tracking-widest hover:border-blue-200 transition-all text-center group relative overflow-hidden">
     <div className={`w-14 h-14 rounded-[5px] flex items-center justify-center mx-auto mb-6 transition-all group-hover:scale-110 group-hover:rotate-3 ${colors[accent]}`}>
      <Icon size={24}/>
     </div>
     <p className="text-[9px] font-black text-slate-400 tracking-widest mb-2 uppercase">{label}</p>
     <p className="text-3xl font-black text-slate-900 uppercase">{value}</p>
     <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
      <Icon size={40} />
     </div>
   </div>
  );
};

export default StudentAttendance;
